import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Pagination } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiTrash, HiPencil, HiBriefcase, HiViewColumns, HiCalendarDays } from 'react-icons/hi2';
import { useDateFormat } from '@/utils/dates';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import SprawaFormModal from '@/components/sprawy/SprawaFormModal';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import {
  EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription,
  DesktopOnly, MobileOnly, MobileCardList, MobileCard, MobileCardHeader, MobileCardBody, MobileCardActions, MobileCardMain, MobileCardTitle,
  FilterBar, StatCardGrid, StatCard, StatValue, StatLabel, StatusBadge, TextMutedSmall,
} from '@/styles/shared.styled';
import {
  useListSprawyQuery,
  useCreateSprawaMutation,
  useUpdateSprawaMutation,
  useDeleteSprawaMutation,
  useListSprawaStatusDefinitionsQuery,
} from '@/store/sprawyApi';
import type { SprawaDto, SprawaPortalStatus, CreateSprawaRequest } from '@/types';
import { SprawaPortalStatus as SprawaPortalStatusEnum } from '@/types';

const PAGE_SIZE = 20;

export default function SprawyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const [page, setPage] = useState(1);
  const [portalStatusFilter, setPortalStatusFilter] = useState<SprawaPortalStatus | ''>('');
  const [statusDefFilter, setStatusDefFilter] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingSprawa, setEditingSprawa] = useState<SprawaDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SprawaDto | null>(null);

  const courtId = searchParams.get('courtId');
  const apelacja = searchParams.get('apelacja');

  const { data, isLoading } = useListSprawyQuery({
    page,
    pageSize: PAGE_SIZE,
    portalStatus: portalStatusFilter || undefined,
    courtId: courtId ? Number(courtId) : undefined,
    apelacja: apelacja || undefined,
    statusDefinitionId: statusDefFilter || undefined,
  });
  const { data: statusDefinitions } = useListSprawaStatusDefinitionsQuery();
  const [createSprawa] = useCreateSprawaMutation();
  const [updateSprawa] = useUpdateSprawaMutation();
  const [deleteSprawa] = useDeleteSprawaMutation();

  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  const openCreate = () => {
    setEditingSprawa(null);
    setShowModal(true);
  };

  const openEdit = (s: SprawaDto) => {
    setEditingSprawa(s);
    setShowModal(true);
  };

  const handleSubmit = async (data: CreateSprawaRequest) => {
    if (editingSprawa) {
      await updateSprawa({ id: editingSprawa.id, ...data });
    } else {
      await createSprawa(data);
    }
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteSprawa(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const renderStatusBadge = (s: SprawaDto) => {
    if (s.statusName && s.statusColor) {
      return <StatusBadge $color={s.statusColor}>{s.statusName}</StatusBadge>;
    }
    return <TextMutedSmall as="span">{t('sprawy.noStatus')}</TextMutedSmall>;
  };

  const sprawyColumns: DataTableColumn<SprawaDto>[] = [
    {
      key: 'signature',
      header: t('sprawy.table.signature'),
      render: (s) => <strong>{s.signature}</strong>,
    },
    { key: 'court', header: t('sprawy.table.court'), render: (s) => s.court ?? '—' },
    { key: 'portalStatus', header: t('sprawy.table.portalStatus'), render: (s) => t(`sprawaStatus.${s.portalStatus}`) },
    { key: 'myStatus', header: t('sprawy.table.myStatus'), render: renderStatusBadge },
    { key: 'documents', header: t('sprawy.table.documents'), render: (s) => s.documentCount, align: 'right' },
    { key: 'posiedzenia', header: t('sprawy.table.posiedzenia'), render: (s) => s.posiedzeniaCount, align: 'right' },
    {
      key: 'lastChange',
      header: t('sprawy.table.lastChange'),
      render: (s) => (s.portalModificationDate ? formatDate(s.portalModificationDate) : '—'),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (s) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(s)} title={t('sprawy.editSprawa')}>
            <HiPencil size="0.875rem" />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(s)} title={t('sprawy.deleteTitle')}>
            <HiTrash size="0.875rem" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        title={t('sprawy.title')}
        subtitle={t('sprawy.subtitle')}
        actions={
          <>
            <Button variant="outline-primary" onClick={() => navigate('/sprawy/calendar')}>
              <HiCalendarDays size="1rem" className="me-2" />
              {t('calendar.switchToCalendar')}
            </Button>
            <Button variant="outline-primary" onClick={() => navigate('/sprawy/board')}>
              <HiViewColumns size="1rem" className="me-2" />
              {t('sprawy.board.switchToBoard')}
            </Button>
            <Button variant="primary" onClick={openCreate}>
              <HiPlus size="1rem" className="me-2" />
              {t('sprawy.addSprawa')}
            </Button>
          </>
        }
      />

      {data && !isLoading && (
        <StatCardGrid>
          <StatCard>
            <StatValue>{data.totalCount}</StatValue>
            <StatLabel>{t('sprawy.stats.total')}</StatLabel>
          </StatCard>
          {statusDefinitions?.slice(0, 3).map((sd) => {
            const count = data.items.filter((s) => s.statusDefinitionId === sd.id).length;
            return (
              <StatCard key={sd.id}>
                <StatValue>{count}</StatValue>
                <StatLabel>{sd.name}</StatLabel>
              </StatCard>
            );
          })}
        </StatCardGrid>
      )}

      <FilterBar>
        <Form.Select
          value={portalStatusFilter}
          onChange={(e) => { setPortalStatusFilter(e.target.value as SprawaPortalStatus | ''); setPage(1); }}
        >
          <option value="">{t('sprawy.allStatuses')}</option>
          {Object.values(SprawaPortalStatusEnum).map((s) => (
            <option key={s} value={s}>{t(`sprawaStatus.${s}`)}</option>
          ))}
        </Form.Select>

        <Form.Select
          value={statusDefFilter}
          onChange={(e) => { setStatusDefFilter(e.target.value); setPage(1); }}
        >
          <option value="">{t('sprawy.allMyStatuses')}</option>
          {statusDefinitions?.map((sd) => (
            <option key={sd.id} value={sd.id}>{sd.name}</option>
          ))}
        </Form.Select>
      </FilterBar>

      {isLoading ? (
        <LoadingSpinner />
      ) : !data?.items.length ? (
        <EmptyState>
          <EmptyStateIcon><HiBriefcase /></EmptyStateIcon>
          <EmptyStateTitle>{t('sprawy.empty.title')}</EmptyStateTitle>
          <EmptyStateDescription>{t('sprawy.empty.description')}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <DesktopOnly>
            <DataTable<SprawaDto>
              ariaLabel={t('sprawy.title')}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate(`/sprawy/${s.id}`)}
              columns={sprawyColumns}
              rows={data.items}
            />
          </DesktopOnly>

          <MobileOnly>
            <MobileCardList>
              {data.items.map((s) => (
                <MobileCard key={s.id} $clickable onClick={() => navigate(`/sprawy/${s.id}`)}>
                  <MobileCardHeader>
                    <MobileCardMain>
                      <MobileCardTitle>{s.signature}</MobileCardTitle>
                      {s.court && <TextMutedSmall>{s.court}</TextMutedSmall>}
                    </MobileCardMain>
                    {renderStatusBadge(s)}
                  </MobileCardHeader>
                  <MobileCardBody>
                    <span>{t(`sprawaStatus.${s.portalStatus}`)}</span>
                    <span>{s.documentCount} {t('sprawy.table.documents')}</span>
                    <span>{s.posiedzeniaCount} {t('sprawy.table.posiedzenia')}</span>
                    {s.portalModificationDate && <span>{formatDate(s.portalModificationDate)}</span>}
                  </MobileCardBody>
                  <MobileCardActions onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline-primary" size="sm" onClick={() => openEdit(s)}>
                      <HiPencil size="0.875rem" />
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(s)}>
                      <HiTrash size="0.875rem" />
                    </Button>
                  </MobileCardActions>
                </MobileCard>
              ))}
            </MobileCardList>
          </MobileOnly>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <small className="text-muted">
                {t('sprawy.showing', {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, data.totalCount),
                  total: data.totalCount,
                })}
              </small>
              <Pagination className="mb-0">
                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                {Array.from({ length: totalPages }, (_, i) => (
                  <Pagination.Item key={i + 1} active={page === i + 1} onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}

      <SprawaFormModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        editing={editingSprawa}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('sprawy.deleteTitle')}
        message={t('sprawy.deleteConfirm', { signature: deleteTarget?.signature })}
        confirmLabel={t('confirm.delete')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageContent>
  );
}
